<template>
  <div>
    <div class="d-flex flex-wrap align-center ga-3 mb-4">
      <NcrmPreferredViewTabs
        v-model="viewModel"
        :items="viewItems"
        :preferred-value="preferredView"
        :aria-label="$t('ncrm.shared.viewPreferences.tabsAriaLabel')"
        @set-default="$emit('set-default')"
      />

      <v-text-field
        v-model="searchModel"
        :label="$t('ncrm.deals.board.search')"
        hide-details
        prepend-inner-icon="mdi-magnify"
        class="ncrm-deal-board-toolbar__search"
      />

      <v-btn
        :variant="hasAdvancedFilters ? 'tonal' : 'text'"
        color="primary"
        class="ncrm-deal-board-toolbar__filter-trigger"
        @click="$emit('open-filters')"
      >
        <v-icon start>mdi-filter-cog-outline</v-icon>
        {{ advancedFilterButtonLabel }}
      </v-btn>

      <v-tooltip location="bottom">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            variant="text"
            color="primary"
            class="ms-auto"
            :disabled="densityCycleDisabled"
            :aria-label="densityCycleAriaLabel"
            @click="$emit('cycle-density')"
          >
            <v-icon>{{ densityStateIcon }}</v-icon>
          </v-btn>
        </template>
        {{ densityCycleTooltip }}
      </v-tooltip>

      <v-btn
        icon="mdi-filter-remove-outline"
        variant="text"
        color="primary"
        :aria-label="$t('ncrm.deals.actions.resetFilters')"
        @click="$emit('reset-filters')"
      />
    </div>

    <div v-if="hasAdvancedFilters" class="d-flex flex-wrap align-center ga-2 mb-4">
      <span class="text-caption text-medium-emphasis">
        {{ $t("ncrm.deals.board.filters.appliedLabel") }}
      </span>
      <v-chip color="secondary">
        {{ advancedFilterLogicChipLabel }}
      </v-chip>
      <v-chip
        v-for="chip in advancedFilterChips"
        :key="chip.key"
        variant="outlined"
        closable
        @click:close="$emit('remove-filter', chip.index)"
      >
        {{ chip.label }}
      </v-chip>
    </div>
  </div>
</template>

<script>
import NcrmPreferredViewTabs from "@/components/ncrm/shared/NcrmPreferredViewTabs.vue";
import { hasDealBoardFilters } from "@/Utile/ncrmDealBoardFilters.mjs";

const normalizeText = (value) => String(value ?? "").trim();

export default {
  name: "NcrmDealBoardToolbar",
  components: {
    NcrmPreferredViewTabs,
  },
  props: {
    view: {
      type: String,
      default: "board",
    },
    search: {
      type: String,
      default: "",
    },
    viewItems: {
      type: Array,
      default: () => [],
    },
    preferredView: {
      type: String,
      default: "board",
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    densityCycleDisabled: {
      type: Boolean,
      default: false,
    },
    densityCycleAriaLabel: {
      type: String,
      default: "",
    },
    densityCycleTooltip: {
      type: String,
      default: "",
    },
    densityStateIcon: {
      type: String,
      default: "mdi-view-grid-outline",
    },
  },
  emits: ["update:view", "update:search", "open-filters", "remove-filter", "reset-filters", "set-default", "cycle-density"],
  computed: {
    viewModel: {
      get() {
        return this.view;
      },
      set(value) {
        this.$emit("update:view", value);
      },
    },
    searchModel: {
      get() {
        return this.search;
      },
      set(value) {
        this.$emit("update:search", value);
      },
    },
    hasAdvancedFilters() {
      return hasDealBoardFilters(this.filters);
    },
    advancedFilterRuleCount() {
      return Array.isArray(this.filters?.rules) ? this.filters.rules.length : 0;
    },
    advancedFilterButtonLabel() {
      if (this.advancedFilterRuleCount > 0) {
        return this.$t("ncrm.deals.board.filters.buttonActive", { count: this.advancedFilterRuleCount });
      }
      return this.$t("ncrm.deals.board.filters.button");
    },
    advancedFilterLogicChipLabel() {
      const value = normalizeText(this.filters?.logic).toLowerCase() === "or" ? "or" : "and";
      return this.$t(`ncrm.deals.board.filters.logic.${value}`);
    },
    advancedFilterChips() {
      return (Array.isArray(this.filters?.rules) ? this.filters.rules : []).map((rule, index) => {
        const fieldLabel = normalizeText(rule?.field_label) || normalizeText(rule?.field_key) || "-";
        const operator = normalizeText(rule?.operator).toLowerCase() || "equals";
        const value = normalizeText(rule?.value) || "-";
        return {
          key: `${fieldLabel}-${operator}-${value}-${index}`,
          index,
          label: `${fieldLabel} ${this.$t(`ncrm.deals.board.filters.operators.${operator}`)} ${value}`,
        };
      });
    },
  },
};
</script>

<style scoped>
.ncrm-deal-board-toolbar__search {
  min-width: 260px;
  max-width: 360px;
}

.ncrm-deal-board-toolbar__filter-trigger {
  min-width: 132px;
}

@media (max-width: 960px) {
  .ncrm-deal-board-toolbar__search {
    min-width: 220px;
    max-width: 320px;
  }
}
</style>
